import type { DragUpdateOptions } from "@project/emerge-types";
import * as THREE from "three";
import { Alignment } from "../enum/Alignment";

/** Default in-drag grid step (mm) while Ctrl/Cmd is held without Shift. */
export const DEFAULT_DRAG_GRID_STEP_MM = 10;

/** Fine in-drag grid step (mm) while Ctrl/Cmd+Shift is held. */
export const FINE_DRAG_GRID_STEP_MM = 1;

export function gridStepFromOptions(options?: DragUpdateOptions): number {
  return options?.gridStepMm ?? DEFAULT_DRAG_GRID_STEP_MM;
}

export function roundToDragGrid(value: number, stepMm: number): number {
  if (stepMm <= 0) {
    return value;
  }
  return Math.round(value / stepMm) * stepMm;
}

/** Clamp to minimum, then snap to the drag grid. */
export function gridDimension(
  value: number,
  min: number,
  stepMm: number,
): number {
  return Math.max(min, roundToDragGrid(value, stepMm));
}

/**
 * Resolve a base dimension from how far its case corner was dragged.
 *
 * Corners sit on the *outer* geometry (`±0.5 * outerDimensions` plus the bracket
 * offset), while the settings field being written is the *base* dimension the
 * blueprint derives that geometry from — the two differ by panel strength, cut
 * offsets, profile heights and rail offsets. Reading the corner position as an
 * absolute half-dimension therefore folds those derived terms into the base on
 * every drag, so even a drag released where it started inflates the case.
 *
 * The case is centred on the origin, so growing a dimension by `d` moves the
 * grabbed corner by `d / 2` on every axis. Mapping the corner movement back at
 * `2 *` is the one factor that makes the rebuilt corner land under the pointer,
 * which in turn makes a refreshed drag context compute a zero residual instead
 * of creeping.
 *
 * `cornerDelta` is the corner's *outward* movement: `sign * (newPos - rest)`
 * with `sign = Math.sign(rest)` for the horizontal axes (`+1` for a top corner).
 *
 * The grid snaps the change rather than the result, so a base the user typed
 * off-grid (or an HE-derived height like 455) survives a drag that returns to
 * its start instead of being pulled onto the nearest 10 mm.
 */
export function dimensionFromCornerDelta(
  base: number,
  cornerDelta: number,
  min: number,
  stepMm: number,
): number {
  return Math.max(min, base + roundToDragGrid(2 * cornerDelta, stepMm));
}

/** Clamp to [min, max], snap to the drag grid, then clamp again so rounding cannot escape bounds. */
export function clampRoundToDragGrid(
  value: number,
  min: number,
  max: number,
  stepMm: number,
): number {
  const clamped = Math.min(max, Math.max(min, value));
  const rounded = roundToDragGrid(clamped, stepMm);
  return Math.min(max, Math.max(min, rounded));
}

/**
 * Snap a margin to the drag grid while staying inside [min, max]. Range
 * endpoints are always valid targets so off-grid corners (e.g. 39 mm on a 10 mm
 * grid) stay reachable.
 */
export function snapMarginToGridInRange(
  value: number,
  min: number,
  max: number,
  stepMm: number,
): number {
  const lo = Math.min(min, max);
  const hi = Math.max(min, max);
  if (stepMm <= 0) {
    return Math.min(hi, Math.max(lo, value));
  }

  const clamped = Math.min(hi, Math.max(lo, value));
  const candidates = new Set<number>([lo, hi]);

  const firstGrid = Math.ceil(lo / stepMm) * stepMm;
  for (let g = firstGrid; g <= hi; g += stepMm) {
    candidates.add(g);
  }
  candidates.add(roundToDragGrid(clamped, stepMm));

  let best = lo;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    if (candidate < lo || candidate > hi) {
      continue;
    }
    const dist = Math.abs(candidate - clamped);
    if (dist < bestDist) {
      bestDist = dist;
      best = candidate;
    }
  }
  return best;
}

/**
 * Rebuild a connector's edge position from its margin (inverse of
 * ProfileBuildStep.updateSettingsFromConnectorDrag).
 */
export function connectorPositionFromMargin(
  margin: number,
  params: {
    edgeDir: THREE.Vector3;
    originalEdgeStart: THREE.Vector3;
    originalEdgeEnd: THREE.Vector3;
    originalEdgeLength: number;
    n: number;
    padding: number;
    alignment: Alignment;
    count: number;
    mirrored: boolean;
  },
): THREE.Vector3 {
  const {
    edgeDir,
    originalEdgeStart,
    originalEdgeEnd,
    originalEdgeLength,
    n,
    padding,
    alignment,
    count,
    mirrored,
  } = params;

  if (count > 1) {
    const denom = 1 - 2 * n;
    const distFromStart =
      margin * denom + originalEdgeLength * n + padding * denom;
    return originalEdgeStart.clone().addScaledVector(edgeDir, distFromStart);
  }

  if (alignment === Alignment.CENTER) {
    const center = originalEdgeStart
      .clone()
      .add(originalEdgeEnd)
      .multiplyScalar(0.5);
    return center.clone().addScaledVector(edgeDir, mirrored ? -margin : margin);
  }

  if ((alignment === Alignment.END) === mirrored) {
    return originalEdgeStart.clone().addScaledVector(edgeDir, margin + padding);
  }

  const distFromStart = originalEdgeLength - margin - padding;
  return originalEdgeStart.clone().addScaledVector(edgeDir, distFromStart);
}
